import { AlertCircle, Loader2, Eye, EyeOff, HelpCircle, Shield, KeyRound } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../services/api';

const API_ROOT = (API_BASE_URL || "").replace(/\/+$/, "");

// Build OAuth start URL that preserves tenant subdomain context.
// On a tenant subdomain (e.g. codehub.mawered.local), use the current origin + /api
// so Traefik forwards with the correct Host header → backend extracts subdomain.
// On root domain (mawered.local / localhost), use API_ROOT directly → no subdomain
// in state → _try_tenant_auth redirects to tenant after Google returns the email.
const RESERVED_SUBDOMAINS = new Set(["mawered", "api", "admin", "www", "traefik", "localhost"]);
function buildOAuthUrl(provider) {
  const hostname = window.location.hostname;
  const parts = hostname.split(".");
  const isTenantSubdomain = parts.length >= 3 && !RESERVED_SUBDOMAINS.has(parts[0]);
  if (isTenantSubdomain) {
    return `${window.location.protocol}//${hostname}/api/v1/auth/${provider}`;
  }
  return `${API_ROOT}/v1/auth/${provider}`;
}

function SsoButton({ href, label, icon, disabled }) {
  const handleClick = () => { window.location.href = href; };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className="w-full flex items-center justify-center gap-2.5 py-2.5 rounded-lg text-sm font-medium transition-colors"
      style={{
        backgroundColor: "var(--lm-canvas)",
        border: "1px solid var(--lm-border)",
        color: "var(--lm-ink)",
        opacity: disabled ? 0.5 : 1,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.borderColor = "#5e6ad2"; }}
      onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--lm-border)")}
    >
      {icon}
      {label}
    </button>
  );
}

function useSamlProviders() {
  const [providers, setProviders] = useState([]);
  useEffect(() => {
    fetch(`${API_ROOT}/v1/sso/providers/public`, {
      headers: { "Content-Type": "application/json" },
    })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setProviders((data || []).filter((p) => p.provider_type === "saml" && p.is_active)))
      .catch(() => {});
  }, []);
  return providers;
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" fill="none">
    <path d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 5.1 29.6 3 24 3 12.9 3 4 11.9 4 23s8.9 20 20 20c11 0 19.7-7.9 19.7-20 0-1.3-.1-2.7-.2-4z" fill="#FFC107"/>
    <path d="M6.3 14.7l7 5.1C15.1 16 19.2 13 24 13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 5.1 29.6 3 24 3 16.3 3 9.7 7.9 6.3 14.7z" fill="#FF3D00"/>
    <path d="M24 43c5.5 0 10.5-1.9 14.4-5.2l-6.6-5.6C29.6 34.1 26.9 35 24 35c-6.1 0-10.7-3.9-11.8-9.3l-7 5.4C8.7 38.7 15.8 43 24 43z" fill="#4CAF50"/>
    <path d="M44.5 20H24v8.5h11.8c-.6 2.5-2.1 4.7-4.2 6.2l6.6 5.6C42.1 36.6 44.5 30.2 44.5 20z" fill="#1976D2"/>
  </svg>
);

const MicrosoftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 21 21" fill="none">
    <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
    <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
  </svg>
);

const inputStyle = {
  backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)",
  borderRadius: "8px", padding: "10px 12px", fontSize: "14px", width: "100%",
  outline: "none", transition: "border-color 0.15s",
};
const inputErrorStyle = { ...inputStyle, borderColor: "var(--lm-error)" };
const labelStyle = { display: "block", fontSize: "12px", fontWeight: "500", marginBottom: "6px", color: "var(--lm-ink-2)" };

const LoginFormCard = ({
  t, language,
  loading, loginProgress,
  error, credentials, handleChange,
  showPassword, setShowPassword,
  fieldErrors, handleSubmit, passwordStrength,
}) => {
  const navigate = useNavigate();
  const pwColor = passwordStrength >= 75 ? "var(--lm-success)" : passwordStrength >= 50 ? "var(--lm-warn)" : "var(--lm-error)";
  const pwLabel = !credentials.password ? '' : passwordStrength >= 75 ? t.strongPassword : passwordStrength >= 50 ? t.mediumPassword : t.weakPassword;
  const samlProviders = useSamlProviders();

  return (
    <>
      {/* Logo + title */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center mb-5">
          <img src="/assets/brand/mawered-navbar-lockup.svg" alt="Mawered" style={{ height: "26px", width: "auto" }} />
        </div>
        <h1 className="text-2xl font-bold mb-1.5" style={{ color: "var(--lm-ink)", letterSpacing: "-0.6px", fontFamily: "Inter, sans-serif" }}>
          {t.title}
        </h1>
        <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>{t.subtitle}</p>
      </div>

      {/* Card */}
      <div className="rounded-2xl" style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}>
        <div className="p-6 pb-4" style={{ borderBottom: "1px solid var(--lm-border)" }}>
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-lg font-semibold" style={{ color: "var(--lm-ink)", letterSpacing: "-0.3px" }}>
              {t.loginTitle}
            </h2>
            <div className="relative group">
              <button
                type="button"
                className="flex items-center justify-center w-8 h-8 rounded-lg transition-colors"
                style={{ color: "var(--lm-ink-3)" }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = "var(--lm-s2)")}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = "transparent")}
              >
                <HelpCircle className="w-4 h-4" />
              </button>
              {/* Tooltip */}
              <div className="absolute right-0 top-10 w-64 p-3 rounded-xl z-20 hidden group-hover:block"
                style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}>
                <div className="flex items-start gap-2 mb-2">
                  <Shield className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "var(--lm-success)" }} />
                  <div>
                    <p className="text-xs font-medium" style={{ color: "var(--lm-ink)" }}>{t.secureUsage}</p>
                    <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t.secureDescription}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <HelpCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#5e6ad2" }} />
                  <div>
                    <p className="text-xs font-medium" style={{ color: "var(--lm-ink)" }}>{t.supportAvailable}</p>
                    <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t.supportDescription}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className="text-xs" style={{ color: "var(--lm-ink-2)" }}>{t.loginSubtitle}</p>
        </div>

        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Progress bar */}
            {loading && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs" style={{ color: "#5e6ad2" }}>
                  <span>{t.loginProgress}</span>
                  <span className="font-mono">{loginProgress}%</span>
                </div>
                <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: "var(--lm-border)" }}>
                  <div className="h-full rounded-full transition-all duration-300" style={{ width: `${loginProgress}%`, backgroundColor: "#5e6ad2" }} />
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-start gap-3 p-3 rounded-xl" style={{ backgroundColor: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)" }}>
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: "var(--lm-error)" }} />
                <div>
                  <p className="text-sm font-medium" style={{ color: "var(--lm-error)" }}>{error}</p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--lm-ink-2)" }}>{t.checkInfoRetry}</p>
                </div>
              </div>
            )}

            {/* Username */}
            <div>
              <label style={labelStyle}>{t.username}</label>
              <input
                id="username" name="username" type="text"
                placeholder={t.usernamePlaceholder}
                value={credentials.username}
                onChange={handleChange}
                disabled={loading}
                style={fieldErrors.username ? inputErrorStyle : inputStyle}
                onFocus={e => (e.target.style.borderColor = fieldErrors.username ? "var(--lm-error)" : "#5e6ad2")}
                onBlur={e => (e.target.style.borderColor = fieldErrors.username ? "var(--lm-error)" : "var(--lm-border)")}
              />
              {fieldErrors.username && (
                <p className="mt-1 text-xs flex items-center gap-1" style={{ color: "var(--lm-error)" }}>
                  <AlertCircle className="w-3 h-3" />{t.requiredField}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label style={labelStyle}>{t.password}</label>
              <div className="relative">
                <input
                  id="password" name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t.passwordPlaceholder}
                  value={credentials.password}
                  onChange={handleChange}
                  disabled={loading}
                  style={{ ...(fieldErrors.password ? inputErrorStyle : inputStyle), paddingRight: "44px" }}
                  onFocus={e => (e.target.style.borderColor = fieldErrors.password ? "var(--lm-error)" : "#5e6ad2")}
                  onBlur={e => (e.target.style.borderColor = fieldErrors.password ? "var(--lm-error)" : "var(--lm-border)")}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  className="absolute top-0 right-0 h-full flex items-center px-3"
                  style={{ color: "var(--lm-ink-3)" }}
                  onMouseEnter={e => (e.currentTarget.style.color = "var(--lm-ink-2)")}
                  onMouseLeave={e => (e.currentTarget.style.color = "var(--lm-ink-3)")}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {fieldErrors.password && (
                <p className="mt-1 text-xs flex items-center gap-1" style={{ color: "var(--lm-error)" }}>
                  <AlertCircle className="w-3 h-3" />{t.requiredField}
                </p>
              )}
              <div className="flex justify-end mt-1.5">
                <button type="button" className="text-xs transition-colors" style={{ color: "#5e6ad2" }}
                  onMouseEnter={e => (e.currentTarget.style.color = "#828fff")}
                  onMouseLeave={e => (e.currentTarget.style.color = "#5e6ad2")}
                  onClick={() => navigate("/forgot-password")}>
                  {t.forgotPassword}
                </button>
              </div>
              {credentials.password && (
                <div className="mt-2 space-y-1">
                  <div className="h-1 rounded-full overflow-hidden" style={{ backgroundColor: "var(--lm-border)" }}>
                    <div className="h-full rounded-full transition-all duration-300" style={{ width: `${passwordStrength}%`, backgroundColor: pwColor }} />
                  </div>
                  <p className="text-xs" style={{ color: pwColor }}>{pwLabel}</p>
                </div>
              )}
            </div>

            {/* SSO Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px" style={{ backgroundColor: "var(--lm-border)" }} />
              <span className="text-xs" style={{ color: "var(--lm-ink-3)" }}>ou continuer avec</span>
              <div className="flex-1 h-px" style={{ backgroundColor: "var(--lm-border)" }} />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <SsoButton href={buildOAuthUrl("google")}    label="Google"    icon={<GoogleIcon />}    disabled={loading} />
              <SsoButton href={buildOAuthUrl("microsoft")} label="Microsoft" icon={<MicrosoftIcon />} disabled={loading} />
            </div>

            {/* Dynamic SAML enterprise providers */}
            {samlProviders.length > 0 && (
              <div className="space-y-2">
                {samlProviders.map((p) => (
                  <SsoButton
                    key={p.id}
                    href={`${API_ROOT}/v1/sso/saml/${p.id}/login`}
                    label={p.name}
                    icon={<KeyRound className="w-4 h-4" style={{ color: "#5e6ad2" }} />}
                    disabled={loading}
                  />
                ))}
              </div>
            )}

            <div style={{ borderTop: "1px solid var(--lm-border)", paddingTop: "20px" }}>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center justify-center gap-2"
                style={{ backgroundColor: "#5e6ad2", color: "#fff", opacity: loading ? 0.6 : 1 }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.backgroundColor = "#828fff"; }}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = "#5e6ad2")}
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" />{t.loginProgress}</> : t.loginButton}
              </button>
              <p className="text-center text-xs mt-3" style={{ color: "var(--lm-ink-3)" }}>
                {t.noAccount || "Pas encore de compte ?"}{" "}
                <a href="/register" className="font-medium transition-colors" style={{ color: "#5e6ad2" }}
                  onMouseEnter={e => (e.target.style.color = "#828fff")}
                  onMouseLeave={e => (e.target.style.color = "#5e6ad2")}>
                  {t.createAccount || "Créer un compte"}
                </a>
              </p>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default LoginFormCard;
